import React, { useEffect, useMemo, useState } from 'react';
import { FaFacebookF, FaInstagram, FaTiktok } from 'react-icons/fa';
import api from '../../services/api';
import { API_BASE_URL } from '../../services/apiBaseUrl';
import './Footer.css';

const DEFAULT_ADEEF_PARAGRAPHS = [
  'Association Des Etudiants Egyptiens en France',
  'Votre partenaire pour le logement et la vie etudiante',
];

const API_BASE = String(API_BASE_URL || '').trim().replace(/\/+$/, '');

function resolveFooterMediaUrl(rawPath) {
  const raw = String(rawPath || '').trim();
  if (!raw) return '';

  if (/^(https?:\/\/|data:|blob:)/i.test(raw)) {
    return raw;
  }

  const normalized = raw.startsWith('/')
    ? raw
    : `/${raw.replace(/^\.?\/+/, '')}`;

  if (normalized.startsWith('/uploads/')) {
    return API_BASE ? `${API_BASE}${normalized}` : normalized;
  }

  return normalized;
}

function parseFooterLogoUrl(imageUrlRaw) {
  const value = String(imageUrlRaw || '').trim();
  if (!value) return { src: '', fitMode: 'contain' };

  const [srcPart, hashPart] = value.split('#');
  const rawMode = String(hashPart || '').trim().toLowerCase();
  const fitMode = ['contain', 'cover', 'fill'].includes(rawMode) ? rawMode : 'contain';

  return {
    src: resolveFooterMediaUrl(srcPart || value),
    fitMode,
  };
}

const Footer = () => {
  const [adeefParagraphs, setAdeefParagraphs] = useState(DEFAULT_ADEEF_PARAGRAPHS);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadFooter = async () => {
      try {
        const response = await api.footer.getPublic();
        const payload = response?.data?.data || {};
        if (cancelled) return;

        const paragraphs = Array.isArray(payload.adeef_paragraphs)
          ? payload.adeef_paragraphs.filter((line) => String(line || '').trim())
          : [];
        const contactRows = Array.isArray(payload.contacts) ? payload.contacts : [];

        setAdeefParagraphs(paragraphs.length > 0 ? paragraphs : DEFAULT_ADEEF_PARAGRAPHS);
        setContacts(contactRows.map((row) => ({
          id: row.id,
          label: row.label,
          href: row.href || null,
          image_url: row.image_url || null,
        })));
      } catch (error) {
        console.error('Failed to load footer content:', error);
      }
    };

    loadFooter();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolvedParagraphs = useMemo(
    () => (adeefParagraphs.length > 0 ? adeefParagraphs : DEFAULT_ADEEF_PARAGRAPHS),
    [adeefParagraphs]
  );

  const resolvedContacts = useMemo(
    () => contacts.filter((contact) => String(contact?.image_url || '').trim()),
    [contacts]
  );

  return (
    <footer className="adeef-footer">
      <div className="container">
        <div className="adeef-footer-grid">
          <div className="adeef-footer-col adeef-footer-col-left">
            <h4>ADEEF</h4>
            {resolvedParagraphs.map((line, index) => (
              <p key={`adeef-line-${index}`}>{line}</p>
            ))}
          </div>

          <div className="adeef-footer-col adeef-footer-col-contact adeef-footer-col-middle">
            <h4>Partners</h4>
            <div className="adeef-contact-logos" aria-label="ADEEF Partners">
              {resolvedContacts.length === 0 ? (
                <p className="adeef-footer-empty-contact">Aucun partenaire publie pour le moment.</p>
              ) : resolvedContacts.map((contact, index) => {
                const { src: logo, fitMode } = parseFooterLogoUrl(contact?.image_url);
                const alt = String(contact?.label || `Partner ${index + 1}`);
                const key = contact.id || `${alt}-${index}`;

                if (contact.href) {
                  return (
                    <a
                      key={key}
                      href={contact.href}
                      target={/^mailto:|^tel:/i.test(contact.href) ? undefined : '_blank'}
                      rel={/^mailto:|^tel:/i.test(contact.href) ? undefined : 'noopener noreferrer'}
                      className="adeef-contact-logo-link"
                      aria-label={alt}
                    >
                      <img
                        src={logo}
                        alt={alt}
                        className="adeef-contact-logo"
                        style={{ objectFit: fitMode }}
                        loading="lazy"
                      />
                    </a>
                  );
                }

                return (
                  <div key={key} className="adeef-contact-logo-link is-static" aria-label={alt}>
                    <img
                      src={logo}
                      alt={alt}
                      className="adeef-contact-logo"
                      style={{ objectFit: fitMode }}
                      loading="lazy"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="adeef-footer-col adeef-footer-col-right">
            <h4>Suivez-nous</h4>
            <div className="adeef-social-wrapper" aria-label="Réseaux sociaux">
              <a 
                href="https://www.facebook.com/ADEEF.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="adeef-social-icon facebook" 
                aria-label="Facebook"
              >
                <span className="tooltip">Facebook</span>
                <span className="icon" aria-hidden="true">
                  <FaFacebookF />
                </span>
              </a>
              <a 
                href="https://www.instagram.com/adeef.official" 
                target="_blank" 
                rel="noopener noreferrer"
                className="adeef-social-icon instagram" 
                aria-label="Instagram"
              >
                <span className="tooltip">Instagram</span>
                <span className="icon" aria-hidden="true">
                  <FaInstagram />
                </span>
              </a>
              <a 
                href="https://www.tiktok.com/@adeef.daily" 
                target="_blank" 
                rel="noopener noreferrer"
                className="adeef-social-icon tiktok" 
                aria-label="TikTok"
              >
                <span className="tooltip">TikTok</span>
                <span className="icon" aria-hidden="true">
                  <FaTiktok />
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="adeef-footer-bottom">
          <p>&copy; {new Date().getFullYear()} ADEEF. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
